///
/// EarthBrowser
///
/// Created by Matt Giger on 10/24/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type ShaderAction } from "@stovokor/shader/ShaderActions"
import { type VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { type VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { StovokorContext } from "@stovokor/StovokorContext"

export interface AttributeLocation {
	attribute: VertexAttribute
	location: number
}

/**
 * A WebGL vertex array object capturing one program's attribute pointers
 * for one geometry plus its element buffer. Binding it replaces the
 * per-draw sequence of bindBuffer / enableVertexAttribArray /
 * vertexAttribPointer / bindBuffer(ELEMENT_ARRAY_BUFFER) with one call.
 *
 * Attribute locations are per program, so a renderer owns one of these for
 * its (shader, geometry) pair and rebuilds it when the shader relinks.
 */
export class VertexArray {
	private context: StovokorContext
	private vao: WebGLVertexArrayObject | null = null
	private readonly attributes: AttributeLocation[]
	private readonly buffers: VertexBuffer[] = []
	private readonly indexBuffer?: IndexBuffer
	// Sum of the GL buffer generations captured at build time; a mismatch
	// means a buffer object was recreated and the pointers are stale
	private generation: number = -1

	constructor(context: StovokorContext, attributes: AttributeLocation[], indexBuffer?: IndexBuffer) {
		this.context = context
		this.attributes = attributes
		this.indexBuffer = indexBuffer
		for(const { attribute } of attributes) {
			if(attribute.buffer && !this.buffers.includes(attribute.buffer)) {
				this.buffers.push(attribute.buffer)
			}
		}
	}

	public destroy(): void {
		if(this.vao) {
			this.context.glState.bindVertexArray(null)
			this.context.gl.deleteVertexArray(this.vao)
			this.vao = null
			// A bind after this creates a fresh, empty array object, which
			// must be rebuilt rather than trusted to hold the old pointers
			this.generation = -1
		}
	}

	/**
	 * Whether this array was built for the buffers the attributes and
	 * geometry currently reference. A geometry that rebuilds its buffers
	 * (PolylineGeometry on a layout change) reassigns its attributes'
	 * `buffer` and replaces its index buffer; an array captured before that
	 * points at deleted objects and must be replaced, not rebound
	 */
	public isCurrent(indexBuffer?: IndexBuffer): boolean {
		if(this.indexBuffer !== indexBuffer) {
			return false
		}
		for(const { attribute } of this.attributes) {
			if(attribute.buffer !== undefined && !this.buffers.includes(attribute.buffer)) {
				return false
			}
		}
		return true
	}

	private currentGeneration(): number {
		let generation = this.indexBuffer?.generation ?? 0
		for(const buffer of this.buffers) {
			generation += buffer.generation
		}
		return generation
	}

	/**
	 * Record the attribute pointers and element binding into the (already
	 * bound) vertex array. Buffers upload any pending data as a side effect
	 * of binding, so the pointers see the final element type and stride.
	 */
	private build(): void {
		for(const { attribute, location } of this.attributes) {
			attribute.bind(location)
		}
		// ELEMENT_ARRAY_BUFFER binding is part of the VAO state
		this.indexBuffer?.bind()
		this.generation = this.currentGeneration()
	}

	/**
	 * Bind for drawing. Pending uploads (uploadData, attribute values added
	 * after construction) are flushed here since the per-attribute bind
	 * that used to do it no longer runs each draw.
	 */
	public bind(): void {
		// Our own array must be bound before anything touches
		// ELEMENT_ARRAY_BUFFER: that binding is VAO state, and an index upload
		// issued while another renderer's array is current would rebind
		// that array's elements to our buffer
		this.vao ??= this.context.gl.createVertexArray()
		this.context.glState.bindVertexArray(this.vao)

		// Flush uploads before the generation check: a buffer whose GL object
		// was released gets a new one here, and the pointers must follow
		for(const buffer of this.buffers) {
			if(buffer.pending) {
				buffer.bind()	// ARRAY_BUFFER binding isn't VAO state
			}
		}
		if(this.indexBuffer?.pending) {
			this.indexBuffer.bind()	// lands in this array's element binding
		}
		if(this.generation !== this.currentGeneration()) {
			this.build()
		}
	}

	public bindAction(): ShaderAction {
		return () => this.bind()
	}
}
